/**
 * @module core/functionality/childViews
 */

const NAME = 'childViews';

import LazyLoader from 'core-libs/nx-lazyloader';

const Props = {
	/**
	 * @method
	 * @param {string} event Event
	 * @param {function} func Callback
	 */
	addChildViewListener(event, func) {
		_.each(this.childViews, (v) => this.listenTo(v, event, func));
	},

	/**
	 * Setup listeners on child views
	 * @method
	 * @private
	 */
	addChildViewListeners() {
		const collection = this.getChildViewCollection();

		if (!collection) {
			throw new Error("Can't use child views without a collection");
		}

		this.removeChildViewListeners(collection);

		// Listen for child events:
		// ------------------------
		// Do we re-render on every add?
		this.listenTo(collection, 'add', this.addedChildViewModel);

		// Sort has fired
		// TODO: Somewhere something is triggering sort - and it isn't backbone (because I know the compartor is not set and backbone will not sort without one)
		// This was causing us to render all the children in an asset gallery, and then immediately reset and re-add them
		// I have searched the codebase and can not find where this is happening, but it is possible this is a variable named event string...
		// Haven't gone deep enough in the debugger to spot it
		// Regardless, nothing should be bypassing this check and triggering `sort`
		if (collection.compartor) {
			this.listenTo(collection, 'sort', this.resetChildViews);
		}
		// Respond to a remove on our collection by removing the view.
		this.listenTo(collection, 'remove', this.removeChildView);
		// And if our collection is reset, update everything
		this.listenTo(collection, 'reset', this.resetChildViews);

		return this;
	},

	/**
	 * @method
	 */
	addedChildViewModel(model) {
		this.childView.reset ? this.render() : this.addChildView(model);
	},

	/**
	 * Internal function to install one child view
	 * @private
	 * @param {Netx.Model} model Model
	 * @returns {Netx.View} Child view
	 */
	addChildView(model, append) {
		if (!model) return;

		// Recycling is good!  Create storage for child views if it doesn't exist yet, indexed by model.cid.
		this.childViews = this.childViews || {};

		let childView = this.childViews[model.cid];
		let newChildView = false;

		// Create main options
		// Loop over each thing in this.childView, excluding this.options.options, and execute
		// any value that may be a function
		const mainOptions = {};
		_.each(_.omit(this.childView, 'options'), (v, k) => {
			let val = v;
			if (_.isFunction(v) && !v.__isView__) {
				val = v.call(this, model);
			}
			mainOptions[k] = val;
		});

		const childCollection = this.getChildViewCollection();
		const isAppViewModel = model.constructor.__name__ === 'AppView';

		let _view;
		// Check and see if it was in the view queue
		if (
			!childView &&
			model.get('queued') &&
			this._viewQueue &&
			isAppViewModel
		) {
			if (this._viewQueue.get(model.id)) {
				_view = model.get('view');
				if (_view) {
					// Get the view
					childView = this.childViews[model.cid] = _view;
					// Make sure it knows who its rightful owner is
					model.collection = this.viewCollection;
					// We are new!
					newChildView = true;
				}
			}
		}

		// Check and see if the view was already created via another `viewCollection`
		if (
			!childView &&
			isAppViewModel &&
			this.viewCollection &&
			!model.isMyOriginalCollection(this.viewCollection)
		) {
			_view = model.get('view');
			if (_view) {
				childView = this.childViews[model.cid] = _view;
				// We are new!
				newChildView = true;
			}
		}

		// New childView
		if (!childView) {
			// Respect max items
			// Do it in here so we do not erroneously think we have exceeded our limits i nthe event of a detach re-add deal
			if (
				mainOptions.maxItems &&
				_.keys(this.childViews).length >= mainOptions.maxItems
			)
				return;

			////////////////////////////////
			// CHILD VIEW SETUP
			////////////////////////////////

			// Child View class:
			const childViewClass = mainOptions.viewClass;

			// if the child class isn't specified assume we do not want to output anything
			if (!childViewClass) return;

			const childViewProto = childViewClass.prototype;
			const childViewModule = childViewProto.module
				? childViewProto.module
				: 'none';

			// Child View ID, if defined.
			const childId = mainOptions.id ? {id: mainOptions.id} : {};

			// Child View options:
			let childViewOptions = {};
			let _childViewOptions = this.childView.options || {};

			// If we are an appView model, extend anything on the model to our childView options
			if (isAppViewModel) {
				_.each(model.attributes.viewOptions, (v, k) => {
					childViewOptions[k] = v;
				});
				childViewOptions.viewSpec = model;
			}

			// Check if any of our child view options are functions
			if (!_.isFunction(_childViewOptions)) {
				_.each(_.omit(_childViewOptions, 'template'), (opt, key) => {
					let val = opt;
					if (
						// Do not execute method overrides - leave them as is
						_.isFunction(opt) &&
						!opt.__isView__
					) {
						val = opt.call(this, model);
					}

					childViewOptions[key] = val;
				});
				if (_childViewOptions.template) {
					childViewOptions.template = _childViewOptions.template;
				}
			}

			// Child View attributes:
			const childViewAttributes = childViewOptions.attributes || {};
			// Check if any of our attributes are functions
			_.each(childViewAttributes, (attr, key, all) => {
				if (!_.isFunction(attr)) return;
				all[key] = attr.call(this, model);
			});
			// If draggable is true add the attibute draggable="true" to the child view
			childViewAttributes.draggable =
				mainOptions.draggable === true ? true : undefined;
			if (mainOptions.href) {
				childViewAttributes.href = mainOptions.href;
			}
			////////////////////////////////
			// !CHILD VIEW SETUP
			////////////////////////////////

			////////////////////////////////
			// CHILD VIEW CREATION
			////////////////////////////////
			let _options = {};

			// Only extend the model/collection if it is not AppView/AppViews
			// For edge cases see Netx.Views.AppModalCrumbsView
			if (childCollection.constructor.__name__ !== 'AppViews') {
				_options.collection = childCollection;
			}
			if (!isAppViewModel) {
				_options.model = model;
			}

			// Put them all together in order:
			// First the options we just defined
			// Then the options parsed in childView setup
			// Then the childId (which is collected from the parsed main options)
			// And lastly, the parsed attributes options
			_.extend(_options, childViewOptions, childId, {
				attributes: childViewAttributes,
			});
			try {
				// Allow one last chance to take full control over what gets extended to the childView
				if (_.isFunction(_childViewOptions)) {
					_.extend(_options, _childViewOptions.call(this, model, _options));
				}
			} catch (ex) {
				console.error(ex);
			}

			// Create
			this.childViews[model.cid] = childView = new childViewClass(_options);

			// If the model present on options is not of type Netx.Models.AppView then do not set the view
			// this will override the correct one
			if (
				isAppViewModel &&
				!(_options.model && _options.model.constructor.__name__ === 'AppView')
			) {
				model.set('view', childView);
			}
			// We are new!
			newChildView = true;
			////////////////////////////////
			// !CHILD VIEW CREATION
			////////////////////////////////
		}
		// ! New childView

		// ////////////////////////////////////
		// // SET TARGET
		// ////////////////////////////////////
		// var target;
		// // If target is explicitly set to false, use this.$el
		// if( mainOptions.target === false ) {
		// 	target = this.$el;
		// }
		// // If set
		// else if( mainOptions.target ) {
		// 	target = this.$( mainOptions.target );
		// }
		// // Target was either not provided or could not be found
		// if ( !target || target.length === 0 ) {
		// 	// Try for .nx-childview or [data-nx-childviews] else use this.$el
		// 	// TODO: this must be better targeted - we need to make sure we do not wind up grabbing a nested childView element
		// 	// TODO: switch over to `.nx-childviews` for better performance
		// 	var childviewTarget = this.$('.nx-childviews, [data-nx-childviews]');
		// 	target = childviewTarget.length ? childviewTarget.first() : this.$el;
		// }
		// ////////////////////////////////////
		// // !SET TARGET
		// ////////////////////////////////////

		////////////////////////////////////
		// POSITION IN DOM
		////////////////////////////////////
		const childModelCount = childCollection.length;
		if (append === true) {
			childView.$el.appendTo(this.$childTargetEl);
		} else {
			// Add the view in the specific order it appears in the collection.
			// NOTE: sometimes we are called by backbone with a model
			// that is identical to, but not the same as, some model in the collection --
			// for instance, if we call set() from a method parser, so the models haven't been unified yet.
			// This line works around that.
			let idx = childCollection.indexOf(childCollection.get(model.id));
			// However, if the model has no ID, the previous search fails, but the simpler case works.
			if (idx === -1) {
				idx = childCollection.indexOf(model);
			}
			// Before
			if (idx === 0) {
				childView.$el.prependTo(this.$childTargetEl);
			}
			// If we are the last view, append to the end of the target:
			else if (idx === childModelCount - 1) {
				// If there is a view all children element, insert before
				childView.$el.appendTo(this.$childTargetEl);
			}
			// collection.indexOf(model) can return -1 if the model has no id.
			else if (idx === -1) {
				if (model.id) {
					// Otherwise, this really shouldn't happen unless something is using addChildView in an unsupported way.
					console.warn('adding child view not in childViews collection', model);
				}
				// Either way, we ought to add what we were asked to add, so append the model's view.
				childView.$el.appendTo(this.$childTargetEl);
			}
			// After
			else {
				// Otherwise, insert this view after the previous model's view
				const previousChildView =
					this.childViews[childCollection.at(idx - 1).cid];
				previousChildView != null
					? previousChildView.$el.after(childView.el)
					: childView.$el.appendTo(this.$childTargetEl);
			}
		}
		////////////////////////////////////
		// !POSITION IN DOM
		////////////////////////////////////

		// Let the child view know it's just been added to its parent view
		childView.trigger('addedToParent', this);

		// Let the view know a child view was added
		this.trigger('addedChildView', childView);

		if (newChildView) {
			this.trigger('addedNewChildView', childView)
				// Listeners for the life of the child view:
				// When removed
				.listenTo(childView, 'remove', () => {
					this.trigger('removedChildView', childView).stopListening(childView);
				});
		}

		// The boolean lastAdded can be set true on a model before it is added to a collecton, to indicate that no more models are being added after this one.
		// This causes the addedChildViews event to be triggered on the parent collection's view after the child model view is rendered.
		// Use this for one-off additions to a view, to trigger post-rendering things (like grid resizing).
		// (Normal childView processing does not need to worry about this.)
		// TODO: This feels like a hack, but I don't have a better idea yet.
		if (model.lastAdded) {
			delete model.lastAdded;
			this.trigger('addedChildViews');
		}

		if (newChildView) {
			// Allow views to access info before the render actually oocurs on viewSpec related views
			if (isAppViewModel) {
				_.defer(() => {
					// Trigger first render. After that, the view will render itself on its own model's events.
					// TODO: views should decide when to render. They can listen for 'addedToParent' if they need to re-render when re-added.
					// For now - as I do not want to fix every view that is depending on this behavior
					if (mainOptions.noRender !== true && !childView.renderedOnce) {
						childView.render();
						!mainOptions.noShow &&
							this.whenShown.then(() => {
								childView.show();
							});
					}
				});
			} else {
				// Trigger first render. After that, the view will render itself on its own model's events.
				// TODO: views should decide when to render. They can listen for 'addedToParent' if they need to re-render when re-added.
				// For now - as I do not want to fix every view that is depending on this behavior
				if (mainOptions.noRender !== true) {
					childView.render();
					!mainOptions.noShow &&
						this.whenShown.then(() => {
							childView.show();
						});
				}
			}
		}

		// Return new child view
		return childView;
	},

	/**
	 * Internal function to add one child view for each collection model
	 * @method
	 * @private
	 * @todo Cleanup/reorganize?
	 */
	addChildViews() {
		// Add child views, if required.
		const addedCVs = [];
		const parentCollection = this.getChildViewCollection();

		// We have a collection to work off of
		if (!parentCollection) return this;

		// Free all views from this.childViews that are not specific to models in parentCollection.
		// (NOTE: we could defer this block for performance if necessary.)
		// NOTE: any other code that stores views in this.childViews (to track rendering & lifecycle)
		// is totally incompatible with the childView system!  We used to do that a lot,
		// but for best results, store such views by calling this.addSubView(view), which stores
		// them in this.subViews{}.
		const modelCids = _.pluck(parentCollection.models, 'cid');

		_.each(this.childViews, (view, cid) => {
			if (modelCids.indexOf(cid) > -1) return;
			view.remove();
			delete this.childViews[cid];
		});

		this.trigger('addingChildViews', this);
		// Opting to break up the adding into chunks
		if (this.childView.breakItUp) {
			const opts = _.isObject(this.childView.breakItUp)
				? this.childView.breakItUp
				: {};
			const totalLength = this.childView.maxItems || parentCollection.length;

			this.breakItUp(
				{
					size: totalLength,
					amount: opts.amount || 50,
					delay: opts.delay || 0,
				},
				(index) => {
					this.addChildView(parentCollection.at(index), true);
				},
				() => {
					this.trigger('addedChildViews', this);
				},
			);
		}
		// We only want to display a certain number of childViews
		else if (this.childView.maxItems) {
			_.each(_.first(parentCollection.models, this.childView.maxItems), (m) =>
				this.addChildView(m, true),
			);
		}
		// Lazy load children
		else if (this.childView.lazyLoad) {
			this.Plugins.Destroy('lazyloader');
			this.initializeLazyLoad();

			const start = this.lazyLoadIndex;
			this.lazyLoadIndex = start + 25;

			const models = parentCollection.models.slice(start, this.lazyLoadIndex);
			models.forEach(_.partial(this.addChildView.bind(this), _, true));

			let additionalOpts = this.childView.lazyLoad;
			if (_.isFunction(additionalOpts)) {
				additionalOpts = additionalOpts.call(this);
			}

			models.length &&
				this.Plugins.Initialize(
					'lazyloader',
					this.$el,
					_.extend(
						{
							childrenEl: this.$childTargetEl[0],
							target: this,
							forwardOnly: true,
						},
						additionalOpts,
					),
				);
		}
		// Show everything we've got
		else {
			parentCollection.each((m) => this.addChildView(m, true));
		}
	},

	/** @method */
	cleanUpChildViews() {
		// Remove any children we may have.
		_.invoke(this.childViews, 'remove');
		delete this.childViews;
	},

	/**
	 * @method
	 */
	detachChildViews() {
		// protect child views from losing their event listeners:
		// remove them from this.$el before we call .html() on it.
		// http://tbranyen.com/post/missing-jquery-events-while-rendering
		_.each(this.childViews, (v) => v.$el.detach());
	},

	/**
	 * @method
	 * @param {Netx.Model} model Model
	 * @return {Netx.View} Child view
	 */
	getChildViewByModel(model) {
		const childViews = this.childViews;

		// Simplest way (should always work)
		let childView = childViews[model.cid];
		if (childView) {
			return childView;
		}

		// But if not, the next simple way is to get it via the order in the child view collection
		const collection = this.getChildViewCollection();
		if (collection) {
			const idx = collection.indexOf(model);
			childView =
				idx > -1 && childViews[collection.at(collection.indexOf(model)).cid];
			if (childView.model === model) return childView;
		}

		// If SOMEHOW nothing above worked, loop ovet child views and find the first one that has
		// the modeltied to it
		for (let key in childViews) {
			if (childViews.hasOwnProperty(key)) {
				childView = childViews[key];
				if (childView.model.cid === model.cid) {
					return childView;
				}
			}
		}
	},

	/**
	 * @method
	 * @return {module:core/lib/netx-collection} Collection
	 */
	getChildViewCollection() {
		// View have a collection
		// This is to avoid the fact we try and push a different (t initialize in some childView parents
		// that is fine in itself - except for when you want loading classes to reflect that collection in the childView
		const childViewCollection = this.childView?.collection;
		// This is to avoid the fact we try and push a different collection in at initialize in some childView parents
		// that is fine in itself - except for when you want loading classes to reflect that collection in the childView
		let parentCollection;
		if (childViewCollection) {
			parentCollection = _.isFunction(childViewCollection)
				? childViewCollection(this.model)
				: childViewCollection;
		}
		return parentCollection || this.collection;
	},

	/**
	 * @method
	 */
	initializeLazyLoad() {
		this.lazyLoadedForward = this.reachedBeginning = true;
		this.reachedEnd = false;
		this.lazyLoadIndex = 0;
	},

	/**
	 * @method
	 * @return {number} Items loading
	 */
	lazyLoadItems() {
		if (this.reachedEnd) return false;

		const parentCollection = this.getChildViewCollection();

		const pageSize = 3;
		const totalItems = parentCollection.length;

		// Always forward
		this.lazyLoadedForward = true;

		this.reachedEnd = this.lazyLoadIndex >= totalItems - 1;

		const start = this.lazyLoadIndex;
		this.lazyLoadIndex = start + pageSize;
		parentCollection.models
			.slice(start, this.lazyLoadIndex)
			.forEach((model) => this.addChildView(model, true));

		return totalItems === 0 ? 0 : false;
	},

	/**
	 * @method
	 * @param {number} oldIndex Old index
	 * @param {number} newIndex New index
	 * @param {object} opts Options
	 * @property {object}  opts                   - Options
	 * @property {boolean} opts.ignoreCollection  - If true, the child view collection model order will not be modified
	 * @property {boolean} opts.ignoreEl          - If true, the child view elements order will not be modified
	 */
	moveChildViewToIndex(oldIndex, newIndex, opts = {}) {
		if (newIndex === oldIndex) return this;

		// Child view collection
		const collection = this.getChildViewCollection();

		// Assume the collection is up to date and it is a view trying to catch up (in cases where multiple views use the same child view collection)
		// in which case we are using the newIndex as our point of reference for data
		const idx = opts.ignoreCollection ? newIndex : oldIndex;

		// Get the model being moved
		const temp = collection.at(idx);

		// Get the child view being moved
		const childView = this.getChildViewByModel(temp);

		if (!childView) return this;

		// Indexes
		let start = oldIndex;
		let end = newIndex;
		// We are modifying everything between the start and end of the move
		// Adjust if old index is after the new index, we want to increment from the lowest index
		// Add 1 to start so that we are not hitting the moving element again - that gets
		if (oldIndex > newIndex) {
			start = newIndex + 1;
			end = oldIndex;
		}

		// Correct placement in DOM
		if (!opts.ignoreEl) {
			// Detach and preserve
			childView.$el.detach();

			// Beginning
			if (newIndex === 0) {
				childView.$el.prependTo(this.$childTargetEl);
			}
			// End
			else if (idx === collection.length - 1) {
				childView.$el.appendTo(this.$childTargetEl);
			}
			// Otherwise, insert this view after the previous model's view
			else {
				const previousChildView = this.getChildViewByModel(
					collection.at(newIndex - 1),
				);
				!_.isUndefined(previousChildView)
					? previousChildView.$el.after(childView.el)
					: childView.$el.appendTo(this.$childTargetEl);
			}
		}

		// Correct placement in child view collection (that way subsequent re-renders will maintain the child view order)
		if (!opts.ignoreCollection && newIndex !== temp.getIndex()) {
			// Remove it, silently
			collection.remove(temp, {silent: true});
			// Add it back in at the new index, silently (we will fire an event when this is over)
			collection.add(temp, {at: newIndex, silent: true});
		}

		// Loop over and emit events to all sibling child views affected by this move
		for (let i = start, cv; i <= end; i++) {
			cv = this.getChildViewByModel(collection.at(i));
			cv && cv.trigger('movedToIndex', cv, this, {from: i - 1, to: i});
		}
		// We moved
		childView.trigger(
			'movedToIndex',
			childView,
			this,
			_.extend({}, opts, {from: oldIndex, to: newIndex}),
		);

		// Corrected placement in collection
		if (!opts.ignoreCollection) {
			collection.trigger(
				'movedItem',
				collection,
				childView,
				this,
				_.extend({}, opts, {from: oldIndex, to: newIndex}),
			);
		}

		return this;
	},

	/**
	 * @method
	 */
	prepareChildViews() {
		if (!this.childView) return;

		// Wait until we have rendered, as childViews could be trying to add themselves before this.$el
		// has anything in it, in which case they wind up in the root of this.$el (for a brief second)
		// This method cleans up any existing listeners as well - so no need to manage that here
		this.addChildViewListeners();

		// Put back child filter string if available
		if (this._childViewFilterStr) {
			const $childSearchFilter = this.$('.nx-filter-children');
			if ($childSearchFilter.length) {
				$childSearchFilter.val(this._childViewFilterStr);
			}
		}

		// Unset before moving forward
		delete this.$childTargetEl;

		const childViewTarget = this.childView.target;
		if (childViewTarget === false) {
			this.$childTargetEl = this.$el;
		} else if (childViewTarget) {
			if (_.isFunction(childViewTarget)) {
				// TODO: support this being a function so that we can have a way to do our split column childViews -
				// really, here we would negate `this.$childTargetEl` and in the `addChildView` method if `this.$childTargetEl` is undefined
				// we would check if it is a function - call the function and pass the child view's model and the view can tell us where it should be
				// appended
			} else {
				this.$childTargetEl = this.$(childViewTarget);
			}
		}
		// Target was either not provided or could not be found
		if (!this.$childTargetEl || this.$childTargetEl.length === 0) {
			const childviewTarget = this.$('.nx-childviews:first');
			this.$childTargetEl = childviewTarget.length
				? childviewTarget.first()
				: this.$el;
		}

		if (this.childView.lazyLoad) {
			this.Plugins.Register({
				lazyloader: {
					name: 'LazyLoader',
					destroyMethod(instance) {
						this.off(instance.destroy());
					},
					initializeMethod(pluginObj, $pluginEl, initOpts, cb) {
						const instance = new LazyLoader(
							$pluginEl[0],
							_.extend({}, pluginObj.options, initOpts),
						);
						cb(instance);
					},
					options: {
						direction: 'horizontal',
					},
				},
			});
		}

		// Add child views, if defined.
		this.addChildViews();
	},

	/**
	 * @method
	 */
	removeAllChildViews() {
		_.invoke(this.childViews, 'remove');
		this.childViews = {};
	},

	/**
	 * Respond to collection remove events by removing the associated childView, if it exists.
	 * @method
	 * @private
	 * @param {Netx.Model} model Model
	 */
	removeChildView(model, collection, opts = {}) {
		const childView = this.childViews[model.cid];
		if (!childView) return;

		this.stopListening(childView);
		childView.remove();
		delete this.childViews[model.cid];
		this.trigger('removedChildView', childView);
		return childView;
	},

	/**
	 * @method
	 * @param {string} event Event
	 * @param {function} func Callback
	 */
	removeChildViewListener(event, func) {
		_.each(this.childViews, (v) => this.stopListening(v, event, func));
	},

	/**
	 * Remove listners on child views
	 * @method
	 */
	removeChildViewListeners(collection) {
		return this.stopListening(collection, 'add', this.addedChildViewModel)
			.stopListening(collection, 'sort', this.resetChildViews)
			.stopListening(collection, 'remove', this.removeChildView)
			.stopListening(collection, 'reset', this.resetChildViews)
			.stopListening('addedChildViews');
	},

	/**
	 * Internal function to reset the child views to match the current collection
	 * @method
	 * @private
	 */
	resetChildViews() {
		const collection = this.getChildViewCollection();

		if (!collection.length) {
			this.Plugins.Destroy('lazyloader');
			this.$childTargetEl.empty();
			_.invoke(this.childViews, 'remove');
			this.childViews = {};
			return this;
		}

		// Detach all child views we have.
		this.detachChildViews();
		// this.trigger('childViewsReset');
		// Add back the ones we want.
		this.addChildViews();

		return this;
	},

	/**
	 * Provided a search event, loop over child views and check a particular key, on its model, for the searched value
	 * @method
	 * @param {event} e Search event
	 */
	searchAndFilterChildren(e) {
		// Search str
		let str = '';
		let attributeKey = [];
		let minimumLength = 2;
		let hasResults = true;

		if (e && e.originalEvent) {
			// This ends here - do not want parent views to get this event
			e.stopPropagation();
			const $target = this.$(e.target);

			// Do not interfere with children
			if (this.$childTargetEl.find($target).length) return;

			if ($target.attr('data-nx-search-key')) {
				attributeKey = $target.attr('data-nx-search-key').split(',');
			}
			minimumLength =
				parseInt($target.attr('data-nx-search-minLength')) || minimumLength;
			str = e.originalEvent.detail || '';
		}

		// Store so we can put back after renders
		this._childViewFilterStr = str;

		// Lowercase it
		str = str.toLowerCase();

		if (str.length < minimumLength) {
			// show all child search views
			_.each(this.childViews, (cv) => cv.$el.show());
			this.el.removeAttribute('data-nx-no-matches');
		} else if (str.length >= minimumLength) {
			let shown = 0;
			// hide all search views that don't match this string
			_.each(this.childViews, (cv) => {
				const model = cv.model;
				let show = attributeKey.some((attrKey) => {
					const value = model.get(attrKey);
					return value != null && value.toLowerCase().includes(str);
				});
				// Fallback to model title
				if (!show) {
					show = model.getLabel().toLowerCase().includes(str);
				}
				show && shown++;
				cv.$el.toggle(show);

				// make sure the first child view
				// is tabable when filtering
				if (show && shown === 1) {
					cv.$el.attr('tabindex', '0');
				}
			});

			hasResults = shown > 0;

			shown === 0
				? this.el.setAttribute('data-nx-no-matches', true)
				: this.el.removeAttribute('data-nx-no-matches');
		}

		this.trigger('filteredChildren', this, hasResults);

		return hasResults;
	},
};

export default function (target) {
	if (!target || target.hasFunctionality(NAME)) return target;

	target.extendFunctionality(Props);
	target.__isChildViews__ = true;

	target.addInitializer((instance, opts = {}) => {
		// As we transition over to react we may find ourselves still stuck with views that extended views
		// that utilize child functionality - rather than making it so we have to jump through a million hoopd
		// or rewrite the thing entirely... just provide a quick way to opt out
		if (opts.disableChildViews === true) return;

		instance.isChildViews = true;
		instance.childViews = {};

		// Simply add an `<nx-search class="@netx/core-filter-children">` to the tmeplate and you can filter the children
		// It defaults to comparing the search string to the model attribute `name` - if you need to compare to a different attribute use `data-nx-search-key="myDesiredKey"`
		// It defaults to a minimum input length of 2 before filtering, if you need to modify this use `data-nx-search-minLength="5"`
		instance.events['search .nx-filter-children'] = 'searchAndFilterChildren';
		/**
		 * Store so that we can put back after renders
		 * @memberOf Netx.View
		 * @alias _childViewFilterStr
		 * @type {string}
		 */
		instance._childViewFilterStr = '';

		instance.childView = opts.childView;

		// Wrap the excluded views method
		instance.getExcludedViews = _.wrap(
			instance.getExcludedViews,
			(getExcludedViews) => {
				return _.pluck(instance.childViews, 'el').concat(
					getExcludedViews.call(instance),
				);
			},
		);

		if (!instance.unsavedChanges) {
			instance.unsavedChanges = (unsavedChanges, noProxy) => {
				let hasUnsavedChanges = false;
				const childViews = instance.childViews;
				const keys = _.keys(childViews);
				for (let i = 0, c = keys.length, childView; i < c; i++) {
					childView = childViews[keys[i]];
					if (childView.unsavedChanges()) {
						return noProxy ? childView : instance;
					}
				}
			};
		}

		// Hey, listen!
		instance
			.on('preRenderHook', instance.detachChildViews)
			.on('postRenderHook', instance.prepareChildViews)
			.on('removeHook', instance.cleanUpChildViews)
			.on('bindCollectionEventsHook', instance.addChildViewListeners)
			//.on('unbindCollectionEventsHook', instance.removeChildViewListeners)
			// cancel loading classes if child views are added.
			// This passes the childView to showLoaded which hurts the loading class removal
			// We no longer check that thing is of model or collection in showLoaded
			// this.listenTo(this, "addedChildView", _.debounce( this.showLoaded, 100 ) );
			.on('addedChildViews', () => {
				instance.showLoaded();
			})
			.on('hidden', () => {
				_.invoke(instance.childViews, 'trigger', 'hiddenByProxy', instance);
			})
			.on('shown', () => {
				_.invoke(instance.childViews, 'trigger', 'shownByProxy', instance);
			});
	}, NAME);

	return target;
}
